import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import type { IApplicationData, IPolicyFormTemplate } from '@/@types/policyFormTemplates';
import type { TPolicySource } from '@/@types/insurance';

// LOCAL_TYPE_JUSTIFIED: hook args/return interfaces are co-located with the hook they describe — no component file, pure hook module
interface IUsePolicyApplicationWizardArgs {
  template: IPolicyFormTemplate;
  orgId: Id<'organisations'>;
  existingPolicyId?: Id<'insurancePolicies'>;
  existingData?: IApplicationData;
  policySource: TPolicySource;
  leadId?: Id<'leads'>;
  companyEmployeeId?: Id<'companyEmployees'>;
}

interface IUsePolicyApplicationWizardReturn {
  currentStep: number;
  values: Record<string, unknown>;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateValue: (key: string, value: unknown) => void;
  saveDraft: () => Promise<void>;
  submitForReview: () => Promise<void>;
  isSaving: boolean;
  isSubmitting: boolean;
  policyId: Id<'insurancePolicies'> | null;
}

export function usePolicyApplicationWizard({
  template,
  orgId,
  existingPolicyId,
  existingData,
  policySource,
  leadId,
  companyEmployeeId,
}: IUsePolicyApplicationWizardArgs): IUsePolicyApplicationWizardReturn {
  const [currentStep, setCurrentStep] = useState(existingData?.currentStep ?? 0);
  const [values, setValues] = useState<Record<string, unknown>>(existingData?.values ?? {});
  const [policyId, setPolicyId] = useState<Id<'insurancePolicies'> | null>(
    existingPolicyId ?? null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveApplicationDraftMutation = useMutation(
    api.insurance.policyApplications.saveApplicationDraft
  );
  const updateApplicationDraftMutation = useMutation(
    api.insurance.policyApplications.updateApplicationDraft
  );
  const submitApplicationMutation = useMutation(api.insurance.policyApplications.submitApplication);

  const totalSteps = template.steps.length;

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(Math.max(0, Math.min(step, totalSteps - 1)));
    },
    [totalSteps]
  );

  const nextStep = useCallback(() => {
    setCurrentStep(s => Math.min(s + 1, totalSteps - 1));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep(s => Math.max(s - 1, 0));
  }, []);

  const updateValue = useCallback((key: string, value: unknown) => {
    setValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const buildCustomerName = (vals: Record<string, unknown>): string => {
    const first = String(vals.applicant_firstName ?? vals.member_firstName ?? '');
    const last = String(vals.applicant_lastName ?? vals.member_lastName ?? '');
    return `${first} ${last}`.trim();
  };

  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      const applicationData: IApplicationData = { currentStep, values };

      if (policyId) {
        await updateApplicationDraftMutation({
          policyId,
          applicationData,
          customerName: buildCustomerName(values),
        });
        toast.success('Draft updated');
      } else {
        const newId = await saveApplicationDraftMutation({
          orgId,
          insuranceTypeId: template.insuranceTypeId!,
          templateId: template._id,
          policySource,
          customerName: buildCustomerName(values),
          leadId,
          companyEmployeeId,
          applicationData,
        });
        setPolicyId(newId);
        toast.success('Draft saved');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save draft';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [
    currentStep,
    values,
    policyId,
    orgId,
    template,
    policySource,
    leadId,
    companyEmployeeId,
    saveApplicationDraftMutation,
    updateApplicationDraftMutation,
  ]);

  const submitForReview = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const applicationData: IApplicationData = { currentStep, values };

      if (policyId) {
        await updateApplicationDraftMutation({ policyId, applicationData });
        await submitApplicationMutation({ policyId });
      } else {
        const newId = await saveApplicationDraftMutation({
          orgId,
          insuranceTypeId: template.insuranceTypeId!,
          templateId: template._id,
          policySource,
          customerName: buildCustomerName(values),
          leadId,
          companyEmployeeId,
          applicationData,
        });
        setPolicyId(newId);
        await submitApplicationMutation({ policyId: newId });
      }
      toast.success('Application submitted for review');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit application';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentStep,
    values,
    policyId,
    orgId,
    template,
    policySource,
    leadId,
    companyEmployeeId,
    saveApplicationDraftMutation,
    updateApplicationDraftMutation,
    submitApplicationMutation,
  ]);

  return {
    currentStep,
    values,
    goToStep,
    nextStep,
    prevStep,
    updateValue,
    saveDraft,
    submitForReview,
    isSaving,
    isSubmitting,
    policyId,
  };
}
